// CRM: normalisation of names, emails, phones, postcodes and dates, plus term and school-year arithmetic.
// Pure; see crmTypes.ts.

const TITLES = new Set(["mr", "mrs", "ms", "miss", "mx", "dr", "prof", "and"]);

/** Lower case, accents removed. */
export const fold = (value: unknown) =>
  String(value ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Name words without titles, punctuation or accents. "O'Brien-Smith" -> "obriensmith". */
export const nameTokens = (name: unknown): string[] =>
  fold(name)
    .replace(/['’\-]/g, "")
    .replace(/[^a-z\s]/g, " ")
    .split(/\s+/)
    .filter((token) => token && !TITLES.has(token));

export const normaliseName = (name: unknown) => nameTokens(name).join(" ");
export const firstNameOf = (name: unknown) => nameTokens(name)[0] ?? "";
export const surnameOf = (name: unknown) => {
  const tokens = nameTokens(name);
  return tokens.length > 1 ? tokens[tokens.length - 1]! : "";
};

/** Placeholder values people type into forms. */
const BLANK = /^(n\/?a|none|-+|\.)$/i;
export const present = (value: unknown) => {
  const text = String(value ?? "").trim();
  return text && !BLANK.test(text) ? text : "";
};

/**
 * Canonical email for matching: lower case, "+tag" removed, and for Gmail the dots removed and googlemail.com
 * folded into gmail.com (Gmail ignores both). "" when it is not an email.
 */
export const canonicalEmail = (email: unknown) => {
  const match = /^([^@\s]+)@([^@\s]+\.[^@\s]+)$/.exec(String(email ?? "").trim().toLowerCase());
  if (!match) return "";
  let local = match[1]!.split("+")[0]!;
  let domain = match[2]!;
  if (domain === "googlemail.com") domain = "gmail.com";
  if (domain === "gmail.com") local = local.replace(/\./g, "");
  return local ? `${local}@${domain}` : "";
};

/** The part before @ without separators, to spot the same person on two providers (jo.smith@gmail / josmith@outlook). */
export const emailHandle = (canonical: string) => canonical.split("@")[0]!.replace(/[._\-]/g, "");

/** UK phone as digits starting with 0 ("+44 7700 900123" -> "07700900123"), or "". */
export const canonicalPhone = (phone: unknown) => {
  let digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("44")) digits = `0${digits.slice(2)}`;
  if (digits.length === 10 && !digits.startsWith("0")) digits = `0${digits}`;
  return digits.length >= 10 && digits.length <= 11 ? digits : "";
};

const POSTCODE = /\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/;

/** UK postcode from a free-text address, as "SL2 3UU", or "". */
export const postcodeOf = (address: unknown) => {
  const match = POSTCODE.exec(String(address ?? "").toUpperCase());
  return match ? `${match[1]} ${match[2]}` : "";
};

/** House number at the start of an address ("12a Orchard Lane" -> "12A"), or "". */
export const houseNumberOf = (address: unknown) => {
  const match = /^\s*(?:flat\s+\d+\w?,?\s*)?(\d+[a-z]?)\b/i.exec(String(address ?? ""));
  return match ? match[1]!.toUpperCase() : "";
};

export const levenshtein = (a: string, b: string) => {
  if (a === b) return 0;
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      current[j] = Math.min(previous[j]! + 1, current[j - 1]! + 1, previous[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    previous = current;
  }
  return previous[b.length]!;
};

/** Same first name, a short form of it (Alex / Alexander) or a one-letter typo (Lily / Lilly). */
export const similarFirstNames = (a: string, b: string) => {
  if (!a || !b) return false;
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (short.length >= 3 && long.startsWith(short)) return true;
  return short.length >= 4 && levenshtein(a, b) <= 1;
};

// ---------------------------------------------------------------------------------------------------------------
// Dates

const LONDON = "Europe/London";

/** YYYY-MM-DD of an instant in London (a date of birth stored as local midnight stays on its day). */
const londonDate = (date: Date) => (isNaN(date.getTime()) ? null : date.toLocaleDateString("en-CA", { timeZone: LONDON }));

/** Registration forms contain typos such as "0224-09-09" or "1111-11-11": treat years outside this range as unknown. */
const plausible = (iso: string | null): string | null => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const year = parseInt(iso.slice(0, 4), 10);
  return year >= 1990 && year <= 2100 ? iso : null;
};

/**
 * YYYY-MM-DD from what the sources store: "2022-05-14", ISO timestamps, "14/05/2022", Date, or a Firestore
 * Timestamp ({ toDate() } or { seconds }). null when it cannot be read or the year is implausible.
 */
export const toIsoDate = (value: unknown): string | null => plausible(readIsoDate(value));

const readIsoDate = (value: unknown): string | null => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return londonDate(value);
  if (typeof value === "string") {
    const text = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
    const uk = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(text);
    if (uk) return `${uk[3]}-${uk[2]!.padStart(2, "0")}-${uk[1]!.padStart(2, "0")}`;
    return londonDate(new Date(text));
  }
  const timestamp = value as { toDate?: () => Date; seconds?: number };
  if (typeof timestamp.toDate === "function") return londonDate(timestamp.toDate());
  if (typeof timestamp.seconds === "number") return londonDate(new Date(timestamp.seconds * 1000));
  return null;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const time = (iso: string) => Date.parse(`${iso.slice(0, 10)}T12:00:00Z`);

export const daysBetween = (from: string, to: string) => Math.round((time(to) - time(from)) / DAY_MS);
export const yearsBetween = (from: string, to: string) => daysBetween(from, to) / 365.25;

export const addMonths = (iso: string, months: number) => {
  const date = new Date(time(iso));
  date.setUTCMonth(date.getUTCMonth() + months);
  return date.toISOString().slice(0, 10);
};

export const addYears = (iso: string, years: number) => addMonths(iso, years * 12);

/** Whole years of age on a date. */
export const ageOn = (dateOfBirth: string, on: string) => {
  const [by, bm, bd] = dateOfBirth.split("-").map(Number) as [number, number, number];
  const [y, m, d] = on.split("-").map(Number) as [number, number, number];
  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
};

/**
 * Children start school (reception) in the September after they turn 4, with the 1 September cut-off: born
 * 1 Sep 2021 - 31 Aug 2022 -> September 2026.
 */
export const schoolStartYear = (dateOfBirth: string) => {
  const year = parseInt(dateOfBirth.slice(0, 4), 10);
  const month = parseInt(dateOfBirth.slice(5, 7), 10);
  return month >= 9 ? year + 5 : year + 4;
};

export const schoolStart = (dateOfBirth: string) => `${schoolStartYear(dateOfBirth)}-09-01`;

// ---------------------------------------------------------------------------------------------------------------
// Preschool terms: ids "2026-autumn" as in preschoolInvoicing.ts (Aug-Dec autumn, Jan-Mar spring, Apr-Jul summer;
// invoices go out ahead of the term they bill).

export type Season = "spring" | "summer" | "autumn";
const SEASON_ORDER: Season[] = ["spring", "summer", "autumn"];
const SEASON_NAME: Record<Season, string> = { spring: "Spring", summer: "Summer", autumn: "Autumn" };

export const termIdFor = (iso: string) => {
  const month = parseInt(iso.slice(5, 7), 10);
  const season: Season = month >= 8 ? "autumn" : month >= 4 ? "summer" : "spring";
  return `${iso.slice(0, 4)}-${season}`;
};

const parseTerm = (termId: string): [number, Season] | null => {
  const match = /^(\d{4})-(spring|summer|autumn)$/.exec(termId);
  return match ? [parseInt(match[1]!, 10), match[2] as Season] : null;
};

export const isTermId = (termId: string | null | undefined) => !!termId && !!parseTerm(termId);

export const nextTermId = (termId: string) => {
  const [year, season] = parseTerm(termId)!;
  const index = SEASON_ORDER.indexOf(season);
  return index === 2 ? `${year + 1}-spring` : `${year}-${SEASON_ORDER[index + 1]}`;
};

/** Chronological order; ids that are not terms sort first instead of failing. */
export const compareTermIds = (a: string, b: string) => {
  const pa = parseTerm(a);
  const pb = parseTerm(b);
  if (!pa || !pb) return (pa ? 1 : 0) - (pb ? 1 : 0);
  return pa[0] - pb[0] || SEASON_ORDER.indexOf(pa[1]) - SEASON_ORDER.indexOf(pb[1]);
};

export const termName = (termId: string) => {
  const parsed = parseTerm(termId);
  return parsed ? `${SEASON_NAME[parsed[1]]} ${parsed[0]}` : termId;
};

export const termSeason = (termId: string): Season | null => parseTerm(termId)?.[1] ?? null;

/** Terms from `first` to `last` inclusive (empty when last is before first). */
export const termsBetween = (first: string, last: string) => {
  const ids: string[] = [];
  if (!parseTerm(first) || !parseTerm(last)) return ids;
  for (let id = first; compareTermIds(id, last) <= 0 && ids.length < 60; id = nextTermId(id)) ids.push(id);
  return ids;
};

/** Sessions a week from "Monday - Morning, Monday - Afternoon, ..." */
export const sessionsPerWeek = (enrolment: string) =>
  (enrolment || "").split(",").filter((part) => /-\s*(morning|afternoon)/i.test(part)).length;

/** Funded hours a week from "15 hour funded" etc. */
export const entitlementHours = (text: string) => {
  const match = /(\d+)\s*hour/i.exec(text || "");
  return match ? parseInt(match[1]!, 10) : 0;
};

export const formatPounds = (pence: number) =>
  `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export const formatPoundsExact = (pence: number) =>
  `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const monthYear = (iso: string) => {
  const date = new Date(time(iso));
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
};
